import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Crown, LogOut, Menu, X, User, Home, Camera, Play, Coins, AlertTriangle, Trophy, TrendingUp, ChevronRight, MessageSquare, Download, RefreshCw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import SubscriberBadge from "@/components/SubscriberBadge";
import NotificationBell from "@/components/NotificationBell";
import { useGamification } from "@/hooks/useGamification";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import logoFallback from "@/assets/logo.png";
import ThemeToggle from "@/components/ThemeToggle";
import { CompleteProfilePopup } from "@/components/CompleteProfilePopup";
import { AssignedNotificationPopup } from "@/components/AssignedNotificationPopup";
import { RequireProfilePhoto } from "@/components/RequireProfilePhoto";
import { PushNotificationBanner } from "@/components/PushNotificationBanner";
import { SubscriptionCancelModal } from "@/components/payment/SubscriptionCancelModal";
import { ProfileCountdownBanner } from "@/components/recovery/ProfileCountdownBanner";
import { UserCouponBanner } from "@/components/UserCouponBanner";
import { RecoveryGamerPopups } from "@/components/recovery/RecoveryGamerPopups";

interface AppLayoutProps {
 children: React.ReactNode;
 activeNav?: string;
 hideNav?: boolean;
}

const AppLayout = ({ children, hideNav = false }: AppLayoutProps) => {
 const navigate = useNavigate();
 const location = useLocation();
 const { user, profile, isAdmin, signOut } = useAuth();
 const { isSubscriber, planId } = useSubscription();
 const { awardPoints, getExperienceProgress } = useGamification();
 const [menuOpen, setMenuOpen] = useState(false);
 const [pointsPopoverOpen, setPointsPopoverOpen] = useState(false);
 const [showPointsBonus, setShowPointsBonus] = useState(false);

 const [appLogoUrl, setAppLogoUrl] = useState("");
 const [appLogoSize, setAppLogoSize] = useState("36");
 const [supportPhone, setSupportPhone] = useState("5511999999999");
 const [supportMsg, setSupportMsg] = useState("Olá! Preciso de ajuda...");
 const [isInstalled, setIsInstalled] = useState(false);

 useEffect(() => {
 const checkInstalled = () => {
 const standalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
 setIsInstalled(standalone);
 };
 
 checkInstalled();
 const mediaQuery = window.matchMedia('(display-mode: standalone)');
 if (mediaQuery.addEventListener) {
 mediaQuery.addEventListener('change', checkInstalled);
 return () => mediaQuery.removeEventListener('change', checkInstalled);
 }
 }, []);

 useEffect(() => {
 fetchBranding();
 }, [profile, isSubscriber]);


 const fetchBranding = async () => {
 const { data } = await (supabase.from("site_settings" as any).select("*") as any);
 if (data) {
 const logo = data.find((s: any) => s.key === "header_logo_url")?.value;
 const size = data.find((s: any) => s.key === "header_logo_size")?.value;
 const phone = data.find((s: any) => s.key === "support_whatsapp")?.value;
 const msg = data.find((s: any) => s.key === "support_message")?.value;
 if (logo) setAppLogoUrl(logo);
 if (size) setAppLogoSize(size);
 if (phone) setSupportPhone(phone);
 if (msg) setSupportMsg(msg);
 }
 };

 const handleSupport = () => {
 window.open(`https://wa.me/${supportPhone}?text=${encodeURIComponent(supportMsg)}`, "_blank");
 };

 const isUserHardcodedAdmin = profile?.email?.toLowerCase().trim() === 'hudsonraonefotografia@gmail.com' || 
 profile?.email?.toLowerCase().trim() === 'hudsonraone@gmail.com';

 const sideNavItems = [
   ...(profile?.is_player ? [{ label: "Meus Registros", path: "/my-records" }] : []),
   ...(profile?.is_player ? [{ label: "Meus Lances", path: "/my-plays" }] : []),
   { label: "Minhas Compras", path: "/my-purchases" },
   ...(isAdmin || isUserHardcodedAdmin ? [{ label: "Admin", path: "/admin" }] : []),
 ];

 const bottomNavItems = [
 { label: "Home", path: "/home", icon: Home },
 { label: "Álbuns", path: "/albums", icon: Camera },
 { label: "Vídeos", path: "/videos", icon: Play },
 { label: "Ranking", path: "/ranking", icon: Trophy },
 { label: "Perfil", path: "/profile", icon: User },
 ];

  const handleLogout = async () => {
    toast.loading("Saindo...", { id: "logout-toast" });
    await signOut();
    toast.success("Até logo!", { id: "logout-toast" });
    
    // Force a hard reload to clear any remaining PWA/SW cache states
    window.location.href = "/";
  };

  const handleForceUpdate = async () => {
    toast.loading("Atualizando aplicativo...", { id: "update-toast" });
    
    // Clear caches
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      } catch (err) {
        console.error("Erro ao limpar cache", err);
      }
    }
    
    // Unregister service workers
    if ('serviceWorker' in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (let registration of registrations) {
          await registration.unregister();
        }
      } catch (err) {
        console.error("Erro ao desregistrar SW", err);
      }
    }
    
    toast.success("App atualizado com sucesso!", { id: "update-toast" });
    
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  const isVirtualUser = profile && user?.email?.endsWith('.ig@byhudsonraone.com') && isSubscriber;
  const needsProfilePhoto = profile && !profile.avatar_url && !isAdmin && !isUserHardcodedAdmin && !isVirtualUser;

 return (
 <div className="min-h-screen bg-background overflow-x-clip">
 {isVirtualUser && <CompleteProfilePopup />}
      <AssignedNotificationPopup />
 {needsProfilePhoto && <RequireProfilePhoto />}
 {/* Header */}
 <UserCouponBanner />
      {!hideNav && (
 <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
 <div className="flex items-center justify-between px-4 md:px-8 h-12 md:h-16 relative">
 <div className="flex h-full items-center relative z-10">
 <img
 src={appLogoUrl || logoFallback}
 alt="Brand Logo"
 style={{ height: `${appLogoSize}px` }}
 className="w-auto object-contain flex-shrink-0 cursor-pointer light-mode-logo-color"
 onClick={() => navigate("/home")}
 />
 </div>

 {/* Desktop nav */}
 <div className="hidden 2xl:flex flex-1 items-center justify-center gap-4 px-4 whitespace-nowrap">
 {sideNavItems.map((item) => (
 <button
 key={item.label}
 onClick={() => navigate(item.path)}
 className={`text-sm font-semibold transition-colors ${
 location.pathname === item.path
 ? "text-primary"
 : "text-muted-foreground hover:text-foreground"
 }`}
 >
 {item.label}
 </button>
 ))}
 </div>

 <div className="flex items-center gap-3">
 {isSubscriber && (
   <div className="flex items-center gap-2 bg-secondary/50 px-2 py-1 rounded-full border border-primary/20 cursor-pointer" onClick={() => navigate("/profile")}>
     <SubscriberBadge planId={planId} />
   </div>
 )}
 {profile && (
 <div className="relative group flex items-center">
 <button 
 onClick={() => setPointsPopoverOpen(!pointsPopoverOpen)}
 className="flex items-center gap-1.5 bg-secondary/50 px-3 py-1 rounded-full border border-primary/20 hover:border-primary/50 transition-all hover:scale-105 active:scale-95"
 >
 <Coins className="w-4 h-4 text-[#FF6A00]" />
 <span className="text-sm font-bold text-foreground">{profile.points || 0}</span>
 </button>

 <AnimatePresence>
 {pointsPopoverOpen && (
 <motion.div 
 key="points-backdrop"
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 className="fixed inset-0 z-40 bg-foreground/10 " 
 onClick={() => setPointsPopoverOpen(false)} 
 />
 )}
 {pointsPopoverOpen && (
 <motion.div
 key="points-popover"
 initial={{ opacity: 0, y: 10, scale: 0.95 }}
 animate={{ opacity: 1, y: 0, scale: 1 }}
 exit={{ opacity: 0, y: 10, scale: 0.95 }}
 className="absolute top-10 right-0 z-50 w-64 bg-card border border-foreground/10 rounded-xl shadow-2xl p-4 overflow-hidden"
 >
 <div className="absolute top-0 right-0 p-8 bg-primary/5 blur-3xl rounded-full" />
 
 <div className="relative">
 <div className="flex justify-between items-center mb-4">
 <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#FF6A01] flex items-center gap-1">
 <TrendingUp className="w-3 h-3" /> Nível {profile.level || 1}
 </span>
 <span className="text-[10px] font-bold text-foreground/40 ">Meta: {((profile.level || 1) * 1000)} XP</span>
 </div>

 <div className="space-y-3">
 <div className="flex justify-between items-end">
 <span className="text-2xl font-display font-black text-foreground ">
 {profile.points || 0}
 <span className="text-[10px] text-foreground/40 not- ml-1">Pts</span>
 </span>
 <span className="text-[10px] font-black text-primary uppercase">
 {Math.floor(getExperienceProgress())}%
 </span>
 </div>

 <div className="h-2 w-full bg-foreground/10 rounded-full overflow-hidden border border-foreground/10 ">
 <motion.div
 initial={{ width: 0 }}
 animate={{ width: `${getExperienceProgress()}%` }}
 className="h-full bg-gradient-to-r from-[#FF6A00] to-[#FF8C33] shadow-[0_0_10px_rgba(255,106,0,0.5)]"
 />
 </div>

 <p className="text-[10px] text-foreground/40 text-center font-medium leading-tight px-2">
 Continue gerando lances e fotos para subir no ranking global! 🔥
 </p>
 
 <button 
 onClick={() => { navigate("/ranking"); setPointsPopoverOpen(false); }}
 className="w-full mt-2 py-2 bg-foreground/10 hover:bg-foreground/10 rounded-xl text-[10px] font-black uppercase tracking-widest text-primary transition-colors border border-primary/10"
 >
 Ver Ranking Global
 </button>
 </div>
 </div>
 </motion.div>
 )}
 </AnimatePresence>
 </div>
 )}
 <ThemeToggle />
 <NotificationBell />
 {profile && !isSubscriber && (
 <button
 onClick={() => navigate("/subscription")}
 className="hidden 2xl:flex items-center gap-1 text-xs font-semibold bg-gradient-primary text-primary-foreground px-4 py-2 rounded-full hover:opacity-90 transition-opacity shadow-[0_4px_15px_rgba(255,106,0,0.3)] animate-in fade-in slide-in-from-right duration-500"
 >
 <Crown className="w-3.5 h-3.5" /> Assinar VIP
 </button>
 )}
        <button
          onClick={handleForceUpdate}
          className="hidden 2xl:flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mr-4"
        >
          <RefreshCw className="w-4 h-4" /> Atualizar App
        </button>

        <button
          onClick={handleLogout}
          className="hidden 2xl:flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <LogOut className="w-4 h-4" /> Sair
        </button>

 {/* Mobile menu toggle */}
 <button 
 onClick={() => setMenuOpen(!menuOpen)} 
 className="2xl:hidden text-foreground p-1 hover:bg-foreground/10 rounded-lg transition-colors"
 >
 {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
 </button>
 </div>
 </div>
 </header>
 )}

 {/* Mobile Floating Menu Overlay - MOVED OUTSIDE HEADER FOR PERFECT CENTERING */}
 <AnimatePresence>
 {menuOpen && (
 <motion.div
 key="mobile-backdrop"
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 onClick={() => setMenuOpen(false)}
 className="fixed inset-0 z-[9990] bg-foreground/10 backdrop-blur-2xl 2xl:hidden"
 />
 )}
 {menuOpen && (
 <motion.div
 key="mobile-menu-wrapper"
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 className="fixed inset-0 z-[9999] pointer-events-none flex items-center justify-center p-6 2xl:hidden"
 >
 <motion.div
 initial={{ opacity: 0, scale: 0.95, y: 20 }}
 animate={{ opacity: 1, scale: 1, y: 0 }}
 exit={{ opacity: 0, scale: 0.95, y: 20 }}
 className="w-full max-w-[320px] pointer-events-auto bg-card border border-border rounded-[32px] overflow-hidden relative shadow-2xl dark:shadow-[0_50px_100px_rgba(0,0,0,1)]"
 >
 <div className="p-8 pb-10 flex flex-col items-center justify-center min-h-[420px]">
 <div className="absolute -top-32 -right-32 w-64 h-64 bg-primary/20 blur-[80px] rounded-full opacity-50" />

 <div className="relative w-full space-y-8">
 <div className="flex flex-col items-center text-center">
 <div className="relative mb-4">
 <div className="w-20 h-20 rounded-2xl bg-foreground/10 border border-foreground/10 flex items-center justify-center overflow-hidden shadow-2xl ring-4 ring-primary/10">
 {profile?.avatar_url ? (
 <img src={profile.avatar_url} className="w-full h-full object-cover" alt="" />
 ) : (
 <User className="w-10 h-10 text-foreground/40 " />
 )}
 </div>
 {isSubscriber && <div className="absolute -bottom-1 -right-1 scale-110"><SubscriberBadge planId={planId} /></div>}
 </div>
 
 <div className="space-y-1">
 <p className="text-[10px] font-black uppercase tracking-[0.4em] text-primary opacity-90 ">VIP PLATFORM</p>
 <h3 className="text-2xl font-display font-black text-foreground  tracking-tighter leading-none">
 {(profile?.full_name || "Usuário").split(" ")[0]} 👑
 </h3>
 <p className="text-[10px] text-foreground/40 font-bold uppercase tracking-widest leading-none pt-1">By Hudson Raone</p>
 </div>
 
 <PushNotificationBanner />
 {isVirtualUser && <CompleteProfilePopup />}
 
 </div>

 <nav className="grid grid-cols-1 gap-2.5 w-full">
 {sideNavItems.map((item, idx) => (
 <motion.button
 key={item.label}
 initial={{ opacity: 0, scale: 0.9 }}
 animate={{ opacity: 1, scale: 1 }}
 transition={{ delay: idx * 0.05 }}
 onClick={() => { navigate(item.path); setMenuOpen(false); }}
 className={`flex items-center justify-between w-full px-5 py-4 rounded-2xl transition-all duration-300 group overflow-hidden relative ${
 location.pathname === item.path
 ? "bg-primary text-primary-foreground shadow-2xl shadow-primary/30"
 : "bg-foreground/[0.03] text-foreground/60 hover:bg-foreground/10 hover:text-foreground border border-foreground/10 "
 }`}
 >
 <span className="text-[11px] font-black uppercase tracking-widest relative z-10">{item.label}</span>
 {location.pathname === item.path ? (
 <div className="w-1.5 h-1.5 rounded-full bg-white relative z-10 shadow-[0_0_10px_white]" />
 ) : (
 <ChevronRight className="w-4 h-4 opacity-20 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
 )}
 </motion.button>
 ))}
 </nav>

 {!isSubscriber && (
 <button
 onClick={() => { navigate("/subscription"); setMenuOpen(false); }}
 className="w-full py-4 rounded-2xl bg-gradient-to-r from-primary to-primary/80 text-foreground font-black uppercase text-[10px] tracking-[0.2em] shadow-xl shadow-primary/20 active:scale-95 transition-transform"
 >
 QUERO SER VIP
 </button>
 )}

 <button
 onClick={() => { handleLogout(); setMenuOpen(false); }}
 className="w-full flex items-center justify-center gap-2 pt-4 text-[9px] font-black uppercase tracking-[0.3em] text-red-500/60 dark:text-red-500/30 hover:text-red-500 transition-colors"
 >
 <LogOut className="w-3.5 h-3.5" /> Encerrar Sessão
 </button>
 </div>
 </div>

 <button 
 onClick={() => setMenuOpen(false)}
 className="absolute top-5 right-5 p-2 rounded-xl bg-foreground/10 text-foreground/40 hover:text-foreground hover:bg-foreground/10 transition-all"
 >
 <X className="w-5 h-5" />
 </button>
 </motion.div>
 </motion.div>
 )}
 </AnimatePresence>

 <motion.main
 key={location.pathname}
 initial={{ opacity: 0, x: 10 }}
 animate={{ opacity: 1, x: 0 }}
 transition={{ duration: 0.3, ease: "easeOut" }}
 className={hideNav ? "pt-0 pb-0" : "pt-16 pb-20"}
 >
 {profile && (!profile.phone || !(profile as any).birth_date || !(profile as any).gender) && location.pathname !== "/profile" && (
 <motion.div 
 initial={{ height: 0, opacity: 0 }}
 animate={{ height: "auto", opacity: 1 }}
 className="px-4 pb-4"
 >
 <div 
 onClick={() => navigate("/profile")}
 className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 flex items-center justify-between gap-4 cursor-pointer hover:bg-orange-500/20 transition-colors"
 >
 <div className="flex items-center gap-3">
 <div className="p-2 rounded-full -white">
 <AlertTriangle className="w-5 h-5" />
 </div>
 <div>
 <h4 className="text-sm font-bold text-foreground">Perfil Incompleto</h4>
 <p className="text-xs text-muted-foreground">Complete seus dados para continuar.</p>
 </div>
 </div>
 <button className="text-xs font-black uppercase tracking-widest text-orange-500 hover:underline">Completar</button>
 </div>
 </motion.div>
 )}

 {user && <ProfileCountdownBanner userId={user.id} />}
 {user && <RecoveryGamerPopups userId={user.id} />}

 {children}
 </motion.main>

 {/* Global bottom navigation */}
 {!hideNav && (
 <nav className="fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-lg border-t border-border">
 <div className="flex items-center justify-around h-16 max-w-md mx-auto md:max-w-2xl px-2">
 {bottomNavItems.map((item) => {
 const Icon = item.icon;
 const isActive = location.pathname === item.path;
 return (
 <button
 key={item.label}
 onClick={() => navigate(item.path)}
 className={`flex flex-col items-center justify-center gap-1 w-full h-full transition-all duration-200 ${
 isActive 
 ? "text-[#FF6A00] scale-[1.05]" 
 : "text-muted-foreground hover:text-foreground"
 }`}
 style={isActive ? { filter: "drop-shadow(0 0 8px rgba(255,106,0,0.5))" } : {}}
 >
 <Icon className="w-5 h-5" />
 <span className="text-[10px] font-medium">{item.label}</span>
 </button>
 );
 })}
 </div>
 </nav>
 )}

 {/* Points Bonus Animation */}
 <AnimatePresence>
 {showPointsBonus && (
 <motion.div
 initial={{ opacity: 0, scale: 0.5, y: 50 }}
 animate={{ opacity: [0, 1, 1, 0], scale: [0.5, 1.2, 1, 1.5], y: -100 }}
 transition={{ duration: 3, ease: "easeOut" }}
 className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none"
 >
 <div className="flex flex-col items-center">
 <div className="relative">
 <motion.div 
 animate={{ rotate: 360 }}
 transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
 className="absolute inset-0 bg-primary/20 blur-[40px] rounded-full"
 />
 <div className="bg-gradient-to-br from-[#FF6A00] to-[#FFB800] p-6 rounded-full shadow-[0_0_50px_rgba(255,106,0,0.5)] border-4 border-foreground/10 relative z-10">
 <Crown className="w-12 h-12 text-foreground fill-foreground" />
 </div>
 </div>
 <motion.span 
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 className="mt-6 font-display font-black text-6xl text-primary drop-shadow-[0_0_20px_rgba(255,106,0,0.8)] "
 >
 +10 PX
 </motion.span>
 <span className="text-foreground font-black tracking-[0.3em] uppercase text-xs mt-2 bg-foreground/10 px-4 py-1 rounded-full backdrop-blur-md border border-foreground/10 ">
 Bônus VIP Ativado
 </span>
 </div>
 </motion.div>
 )}
 </AnimatePresence>

 

  <SubscriptionCancelModal />
 </div>
 );
};

export default AppLayout;
